import React, { useState } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/authSlice';
import { useAppSettings } from '../hooks/useAppSettings';

const navGroups = [
  { label: 'Main', items: [
    { path: '/developer/dashboard',    icon: '🏠', label: 'Dashboard' },
    { path: '/developer/applications', icon: '📂', label: 'All Applications' },
  ]},
  { label: 'Masters', items: [
    { path: '/developer/masters/noc',             icon: '📜', label: 'NOC Types' },
    { path: '/developer/masters/department',      icon: '🏢', label: 'Departments' },
    { path: '/developer/masters/designation',     icon: '👤', label: 'Designations' },
    { path: '/developer/masters/employee',        icon: '👷', label: 'Employees' },
    { path: '/developer/masters/document-demand', icon: '📁', label: 'Document Demands' },
    { path: '/developer/masters/notice',          icon: '📢', label: 'Notices' },
    { path: '/developer/masters/users',           icon: '👥', label: 'Citizens' },
  ]},
  { label: 'RBAC', items: [
    { path: '/developer/rbac/roles',            icon: '🔒', label: 'Roles' },
    { path: '/developer/rbac/menus',            icon: '📋', label: 'Menus' },
    { path: '/developer/rbac/menu-permissions', icon: '🎛️', label: 'Permissions' },
    { path: '/developer/operator-manager',      icon: '👷', label: 'Operator Manager' },
    { path: '/developer/noc-access-config',    icon: '🔑', label: 'NOC Access Config' },
  ]},
  { label: 'Workflow', items: [
    { path: '/developer/workflow-routing',     icon: '⚙️', label: 'Workflow Routing' },
    { path: '/developer/sarai-zone-routing',   icon: '🏘️', label: 'Sarai Zone Routing' },
    { path: '/developer/flow-tracking',        icon: '🔀', label: 'Flow Tracking' },
  ]},
  { label: 'Analytics', items: [
    { path: '/developer/reports',      icon: '📊', label: 'Reports' },
    { path: '/developer/noc-report',    icon: '📋', label: 'NOC Report' },
    { path: '/developer/dept-report',      icon: '🏢', label: 'Dept Report' },
    { path: '/developer/dept-noc-report',  icon: '📊', label: 'Dept NOC Report' },
    { path: '/developer/feedbacks', icon: '📝', label: 'Feedbacks' },
     { path: '/developer/pending-report',  icon: '⏰', label: 'Pending Report' },
    { path: '/developer/activity-logs',    icon: '📋', label: 'Activity Logs' },
  ]},
  { label: 'System', items: [
    { path: '/developer/theme',  icon: '🎨', label: 'Theme Settings' },
    { path: '/developer/system', icon: '⚙️', label: 'System Settings' },
  ]},
  { label: 'Account', items: [
    { path: '/developer/change-password', icon: '🔒', label: 'Change Password' },
  ]},
];

export default function DevLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { user } = useSelector(s => s.auth);
  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const settings  = useAppSettings();
  const handleLogout = () => { dispatch(logout()); navigate('/login'); };

  const headerColor = settings?.header_color || '#1a202c';

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: settings?.bg_color || '#f7fafc' }}>

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} overflow-y-auto`}
        style={{ background: `linear-gradient(180deg, ${headerColor} 0%, ${headerColor}cc 100%)` }}>
        <div className="flex flex-col min-h-full">

          {/* Logo */}
          <div className="px-6 py-5 border-b border-white/10 sticky top-0 z-10" style={{ background: headerColor }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-xl">💻</div>
              <div>
                <p className="text-white font-bold text-sm truncate max-w-[140px]">
                  {settings?.app_name || 'Gov NOC Portal'}
                </p>
                <p className="text-white/70 text-xs">Developer Panel</p>
              </div>
            </div>
          </div>

          {/* User info */}
          <div className="px-4 py-3 border-b border-white/10">
            <p className="text-xs text-white/60">Developer</p>
            <p className="text-white font-medium text-sm truncate">{user?.name}</p>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-4">
            {navGroups.map(group => (
              <div key={group.label}>
                <p className="text-white/50 text-xs font-semibold uppercase tracking-wider px-2 mb-1">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.items.map(item => (
                    <NavLink key={item.path} to={item.path}
                      className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                      <span>{item.icon}</span><span>{item.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-white/10">
            <button onClick={handleLogout} className="sidebar-link w-full text-left">
              <span>🚪</span><span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-4 shadow-sm">
          <button className="lg:hidden text-gray-500 text-xl" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
          <h2 className="text-gray-700 font-semibold text-sm flex-1">
            {settings?.app_name || 'Government NOC Management System'}
          </h2>
          <span className="text-xs bg-orange-50 border border-orange-200 text-orange-700 px-3 py-1 rounded-full">Developer</span>
          <div className="relative">
            <button onClick={() => setDropdownOpen(p => !p)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 px-3 py-1.5 rounded-xl hover:bg-gray-100 transition-all">
              <div className="w-7 h-7 bg-orange-100 rounded-full flex items-center justify-center text-orange-700 font-bold text-xs">
                {user?.name?.[0]?.toUpperCase() || 'D'}
              </div>
              <span className="hidden sm:block max-w-[120px] truncate font-medium">{user?.name}</span>
              <span className="text-gray-400 text-xs">▾</span>
            </button>
            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100">
                  <p className="text-xs font-semibold text-gray-800 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-400">Developer</p>
                </div>
                <Link to="/developer/change-password" onClick={() => setDropdownOpen(false)}
                  className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                  🔒 Change Password
                </Link>
                <button onClick={() => { setDropdownOpen(false); handleLogout(); }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                  🚪 Logout
                </button>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 md:p-6"><Outlet /></main>
      </div>
    </div>
  );
}